import type { MarketingLang, MarketingPageId } from "./routes";

export interface PageSeoCopy {
  title: string;
  description: string;
  h1: string;
}

export const PAGE_SEO: Record<MarketingPageId, Record<MarketingLang, PageSeoCopy>> = {
  home: {
    fr: {
      title: "OwnerFlow | Réseau privé et inventaire confidentiel",
      description:
        "Partagez vos disponibilités en temps réel avec vos apporteurs d'affaires. Adresses, propriétaires et marges protégés. Réseau fermé, zéro visibilité publique, aucune commission sur vos réservations.",
      h1: "Vos biens. Votre réseau de confiance. Plus d'opportunités.",
    },
    en: {
      title: "OwnerFlow | Private network and confidential inventory",
      description:
        "Share live availability with your trusted referrers. Addresses, owners and margins stay protected. Closed network, zero public visibility, no commission on your bookings.",
      h1: "Your properties. Your trusted network. More opportunities.",
    },
    es: {
      title: "OwnerFlow | Red privada e inventario confidencial",
      description:
        "Comparta su disponibilidad en tiempo real con sus prescriptores de confianza. Direcciones, propietarios y márgenes protegidos. Red cerrada, cero visibilidad pública, ninguna comisión sobre sus reservas.",
      h1: "Sus inmuebles. Su red de confianza. Más oportunidades.",
    },
  },
  ownerAccount: {
    fr: {
      title: "Compte Propriétaire OwnerFlow | Gérer vos biens et votre réseau",
      description:
        "Gérez vos propres biens, calendrier, finances et incidents, puis publiez sélectivement vers vos partenaires — sans portefeuille de mandants.",
      h1: "Gérez vos biens, faites-les circuler dans votre réseau.",
    },
    en: {
      title: "OwnerFlow Owner account | Manage your properties and network",
      description:
        "Manage your own properties, calendar, finances and issues, then publish selectively to your partners — without a portfolio of mandating owners.",
      h1: "Manage your properties, circulate them through your network.",
    },
    es: {
      title: "Cuenta Propietario OwnerFlow | Gestione sus inmuebles y su red",
      description:
        "Gestione sus propios inmuebles, calendario, finanzas e incidencias, y publíquelos de forma selectiva hacia sus socios, sin cartera de mandantes.",
      h1: "Gestione sus inmuebles y hágalos circular en su red.",
    },
  },
  managerAccount: {
    fr: {
      title: "Compte Gestionnaire OwnerFlow | Exploitation, portail et réseau",
      description:
        "Centralisez l'exploitation pour vos mandants, offrez-leur un portail, et connectez le portefeuille à votre réseau professionnel.",
      h1: "Exploitez le portefeuille, donnez de la visibilité, étendez le réseau.",
    },
    en: {
      title: "OwnerFlow Manager account | Operations, portal and network",
      description:
        "Centralize operations for mandating owners, give them a portal, and connect the portfolio to your professional network.",
      h1: "Run the portfolio, give visibility, grow the network.",
    },
    es: {
      title: "Cuenta Gestor OwnerFlow | Explotación, portal y red",
      description:
        "Centralice la explotación para sus mandantes, ofrézcales un portal y conecte la cartera a su red profesional.",
      h1: "Gestione la cartera, dé visibilidad y amplíe la red.",
    },
  },
  management: {
    fr: {
      title: "Logiciel de gestion locative connecté à votre réseau | OwnerFlow",
      description:
        "Gérez biens, disponibilités, réservations, propriétaires, incidents et finances, puis connectez votre portefeuille à votre réseau professionnel.",
      h1: "Centralisez votre activité locative sans isoler votre inventaire.",
    },
    en: {
      title: "Rental management software connected to your network | OwnerFlow",
      description:
        "Manage properties, availability, bookings, owners, maintenance and finances, then connect your portfolio to your professional network.",
      h1: "Centralize rental operations without isolating your inventory.",
    },
    es: {
      title: "Software de gestión de alquiler conectado a su red | OwnerFlow",
      description:
        "Gestione propiedades, disponibilidad, reservas, propietarios, incidencias y finanzas, y conecte su cartera a su red profesional.",
      h1: "Centralice su actividad de alquiler sin aislar su inventario.",
    },
  },
  network: {
    fr: {
      title: "Réseau de partenaires pour professionnels de la location | OwnerFlow",
      description:
        "Invitez vos partenaires de confiance, développez votre réseau direct et indirect et centralisez vos échanges sans créer une marketplace ouverte.",
      h1: "Transformez vos partenaires en réseau numérique structuré.",
    },
    en: {
      title: "Partner network for rental professionals | OwnerFlow",
      description:
        "Invite trusted partners, grow your direct and indirect network, and keep conversations in one place — without creating an open marketplace.",
      h1: "Turn existing partners into a structured digital network.",
    },
    es: {
      title: "Red de socios para profesionales del alquiler | OwnerFlow",
      description:
        "Invite a socios de confianza, desarrolle su red directa e indirecta y centralice los intercambios sin crear un marketplace abierto.",
      h1: "Convierta a sus socios en una red digital estructurada.",
    },
  },
  sharing: {
    fr: {
      title: "Partage d'inventaire locatif et diffusion en cascade | OwnerFlow",
      description:
        "Choisissez les partenaires, le prix transmis, les permissions et la profondeur de repartage. Mesurez la portée et révoquez chaque branche.",
      h1: "Faites circuler chaque bien, selon vos règles.",
    },
    en: {
      title: "Rental inventory sharing and cascade distribution | OwnerFlow",
      description:
        "Choose partners, the transmitted price, permissions and reshare depth. Measure reach and revoke any branch.",
      h1: "Circulate every property, on your terms.",
    },
    es: {
      title: "Compartir inventario de alquiler y difusión en cascada | OwnerFlow",
      description:
        "Elija socios, el precio transmitido, los permisos y la profundidad de redistribución. Mida el alcance y revoque cada rama.",
      h1: "Haga circular cada propiedad, según sus reglas.",
    },
  },
  requests: {
    fr: {
      title: "Demandes de réservation traçables entre partenaires | OwnerFlow",
      description:
        "Vérifiez les disponibilités, recevez ou envoyez une demande, conservez son origine et convertissez-la en réservation depuis OwnerFlow.",
      h1: "Transformez les opportunités du réseau en réservations traçables.",
    },
    en: {
      title: "Traceable booking requests between partners | OwnerFlow",
      description:
        "Check availability, send or receive a request, keep its origin, and convert it into a booking from OwnerFlow.",
      h1: "Turn network opportunities into traceable bookings.",
    },
    es: {
      title: "Solicitudes de reserva trazables entre socios | OwnerFlow",
      description:
        "Compruebe la disponibilidad, envíe o reciba una solicitud, conserve su origen y conviértala en reserva desde OwnerFlow.",
      h1: "Convierta las oportunidades de la red en reservas trazables.",
    },
  },
  messaging: {
    fr: {
      title: "Messagerie professionnelle pour partenaires locatifs | OwnerFlow",
      description:
        "Centralisez conversations, sujets, accusés de lecture et non-lus avec vos partenaires, au même endroit que les biens et demandes.",
      h1: "Gardez chaque échange dans le contexte du partenariat.",
    },
    en: {
      title: "Professional messaging for rental partners | OwnerFlow",
      description:
        "Keep conversations, subjects, read receipts and unread messages with partners, next to the properties and requests they relate to.",
      h1: "Keep every exchange in the context of the partnership.",
    },
    es: {
      title: "Mensajería profesional para socios de alquiler | OwnerFlow",
      description:
        "Centralice conversaciones, asuntos, confirmaciones de lectura y no leídos con sus socios, junto a las propiedades y solicitudes.",
      h1: "Conserve cada intercambio en el contexto de la colaboración.",
    },
  },
  ownerPortal: {
    fr: {
      title: "Portail propriétaires | OwnerFlow",
      description:
        "Offrez à vos mandants un espace pour suivre leurs biens, réservations, incidents et dépenses, sans accéder à l'outil d'exploitation.",
      h1: "Un portail clair pour chaque propriétaire mandant.",
    },
    en: {
      title: "Owner portal | OwnerFlow",
      description:
        "Give mandating owners a space to follow their properties, bookings, issues and expenses, without access to the operations tool.",
      h1: "A clear portal for every mandating owner.",
    },
    es: {
      title: "Portal de propietarios | OwnerFlow",
      description:
        "Ofrezca a sus mandantes un espacio para seguir sus propiedades, reservas, incidencias y gastos, sin acceder a la herramienta de explotación.",
      h1: "Un portal claro para cada propietario mandante.",
    },
  },
  partnerAccount: {
    fr: {
      title: "Compte Partenaire OwnerFlow | Accéder à un inventaire partagé",
      description:
        "Accédez aux biens autorisés par vos partenaires, repartagez-les si permis et envoyez des demandes de réservation depuis un compte dédié.",
      h1: "Rejoignez le réseau, même sans bien à gérer.",
    },
    en: {
      title: "OwnerFlow Partner account | Access shared inventory",
      description:
        "Access properties your partners authorize, reshare them when allowed, and send booking requests from a dedicated account.",
      h1: "Join the network, even without properties of your own.",
    },
    es: {
      title: "Cuenta Socio OwnerFlow | Acceder a un inventario compartido",
      description:
        "Acceda a las propiedades autorizadas por sus socios, redistribúyalas si está permitido y envíe solicitudes de reserva desde una cuenta dedicada.",
      h1: "Únase a la red, incluso sin propiedades propias.",
    },
  },
  pricing: {
    fr: {
      title: "Tarifs OwnerFlow | Propriétaire, Gestionnaire et Partenaire",
      description:
        "Propriétaire dès 3,99 € HT, Gestionnaire dès 4,99 € HT et Partenaire gratuit. Tarification dégressive uniquement sur les biens que vous gérez.",
      h1: "Vous payez uniquement pour les biens que vous gérez.",
    },
    en: {
      title: "OwnerFlow pricing | Owner, Manager and Partner",
      description:
        "Owner from €3.99 excl. VAT, Manager from €4.99 and Partner free. Graduated pricing only on properties you manage.",
      h1: "Only pay for the properties you manage.",
    },
    es: {
      title: "Precios OwnerFlow | Propietario, Gestor y Socio",
      description:
        "Propietario desde 3,99 € sin IVA, Gestor desde 4,99 € y Socio gratis. Precio decreciente solo para las propiedades gestionadas.",
      h1: "Pague únicamente por las propiedades que gestiona.",
    },
  },
  about: {
    fr: {
      title: "À propos d'OwnerFlow | Le réseau locatif professionnel",
      description:
        "OwnerFlow réunit la gestion locative et un réseau de partenaires de confiance. Édité par IntraPro, sans commission sur les réservations.",
      h1: "Conçu pour les professionnels de la location",
    },
    en: {
      title: "About OwnerFlow | The professional rental network",
      description:
        "OwnerFlow brings together rental management and a trusted partner network. Published by IntraPro, with no commission on bookings.",
      h1: "Built for rental professionals",
    },
    es: {
      title: "Acerca de OwnerFlow | La red profesional de alquiler",
      description:
        "OwnerFlow reúne la gestión de alquiler y una red de socios de confianza. Editado por IntraPro, sin comisión sobre las reservas.",
      h1: "Diseñado para profesionales del alquiler",
    },
  },
  contact: {
    fr: {
      title: "Contacter OwnerFlow",
      description:
        "Une question commerciale, un accompagnement ou un besoin produit : écrivez à l'équipe OwnerFlow.",
      h1: "Parlons de votre activité et de votre réseau",
    },
    en: {
      title: "Contact OwnerFlow",
      description: "A commercial question, onboarding help or a product need: write to the OwnerFlow team.",
      h1: "Let's talk about your activity and your network",
    },
    es: {
      title: "Contactar OwnerFlow",
      description:
        "Una pregunta comercial, un acompañamiento o una necesidad de producto: escriba al equipo OwnerFlow.",
      h1: "Hablemos de su actividad y de su red",
    },
  },
  help: {
    fr: {
      title: "Guide OwnerFlow | Réseau, biens et réservations",
      description: "Guides OwnerFlow : biens, réseau, réservations, tarifs et compte.",
      h1: "Comment pouvons-nous vous aider ?",
    },
    en: {
      title: "OwnerFlow Guide | Network, properties and bookings",
      description: "OwnerFlow guides: properties, network, bookings, pricing and account.",
      h1: "How can we help you?",
    },
    es: {
      title: "Guía OwnerFlow | Red, propiedades y reservas",
      description: "Guías OwnerFlow: propiedades, red, reservas, precios y cuenta.",
      h1: "¿Cómo podemos ayudarle?",
    },
  },
  privacy: {
    fr: {
      title: "Politique de confidentialité | OwnerFlow",
      description: "Comment OwnerFlow traite vos données personnelles (RGPD, LOPDGDD, LSSI-CE).",
      h1: "Politique de confidentialité",
    },
    en: {
      title: "Privacy policy | OwnerFlow",
      description: "How OwnerFlow processes your personal data (GDPR, LOPDGDD, LSSI-CE).",
      h1: "Privacy policy",
    },
    es: {
      title: "Política de privacidad | OwnerFlow",
      description: "Cómo OwnerFlow trata sus datos personales (RGPD, LOPDGDD, LSSI-CE).",
      h1: "Política de privacidad",
    },
  },
  terms: {
    fr: {
      title: "Conditions d'utilisation | OwnerFlow",
      description: "Conditions d'utilisation de la plateforme OwnerFlow, conformément au droit espagnol.",
      h1: "Conditions d'utilisation",
    },
    en: {
      title: "Terms of use | OwnerFlow",
      description: "Terms of use of the OwnerFlow platform, in accordance with Spanish law.",
      h1: "Terms of use",
    },
    es: {
      title: "Condiciones de uso | OwnerFlow",
      description: "Condiciones de uso de la plataforma OwnerFlow, conforme al derecho español.",
      h1: "Condiciones de uso",
    },
  },
};
